import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../api/client'
import type { components } from '../api/schema'

/**
 * Knowledge notes (spec 2026-10-10-06): titled notes on an asset, the knowledge
 * that applies to an asset (its own, its ancestors' and the organization's), and
 * the organization-level notes.
 */

export type KnowledgeNote = components['schemas']['KnowledgeNote']
export type KnowledgeKind = components['schemas']['KnowledgeKind']
export type TiptapDocument = components['schemas']['TiptapDocument']
export type PageVersion = components['schemas']['PageVersion']
/** A note version; one applied through a proposal says where it came from. */
export type NoteVersion = PageVersion & { origin?: components['schemas']['NoteVersionOrigin'] }

export const KNOWLEDGE_KINDS: readonly KnowledgeKind[] = ['note', 'rule', 'procedure']

export interface NoteInput {
    title?: string
    kind?: KnowledgeKind
    applies_to_subtree?: boolean
    content?: TiptapDocument
    text_content?: string
}

function fail(error: unknown): never {
    const e = error as { title?: string; message?: string; fields?: Array<{ message?: string }> } | undefined
    throw new Error(e?.fields?.[0]?.message || e?.title || e?.message || 'Request failed')
}

const keys = {
    applicable: (org: string, assetUid: string) => ['knowledge', org, 'applicable', assetUid] as const,
    org: (org: string, q: string, kind: string) => ['knowledge', org, 'org', q, kind] as const,
    versions: (org: string, assetUid: string, noteUid: string) => ['knowledge', org, 'versions', assetUid, noteUid] as const,
}

/** Everything that applies to the asset; the inherited notes are flagged. */
export function useAssetKnowledge(org: string, assetUid: string | undefined) {
    return useQuery({
        queryKey: keys.applicable(org, assetUid ?? ''),
        enabled: !!assetUid,
        queryFn: async (): Promise<KnowledgeNote[]> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/assets/{assetUid}/knowledge', {
                params: { path: { orgCode: org, assetUid: assetUid! } },
            })
            if (error) fail(error)
            return data?.notes ?? []
        },
    })
}

/** Every note in reach, the organization's and the assets', optionally searched. */
export function useOrganizationKnowledge(org: string, q: string, kind: string) {
    return useQuery({
        queryKey: keys.org(org, q, kind),
        queryFn: async (): Promise<KnowledgeNote[]> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/knowledge', {
                params: {
                    path: { orgCode: org },
                    query: { q: q || undefined, kind: (kind || undefined) as KnowledgeKind | undefined, source: 'all' },
                },
            })
            if (error) fail(error)
            return data?.notes ?? []
        },
    })
}

export function useNoteVersions(org: string, assetUid: string, noteUid: string, enabled: boolean) {
    return useQuery({
        queryKey: keys.versions(org, assetUid, noteUid),
        enabled,
        queryFn: async (): Promise<NoteVersion[]> => {
            const { data, error } = await apiClient.GET(
                '/api/v1/organizations/{orgCode}/assets/{assetUid}/notes/{noteUid}/versions',
                { params: { path: { orgCode: org, assetUid, noteUid } } },
            )
            if (error) fail(error)
            return data?.versions ?? []
        },
    })
}

function useInvalidateKnowledge(org: string) {
    const queryClient = useQueryClient()

    return () => {
        void queryClient.invalidateQueries({ queryKey: ['knowledge', org] })
        // The asset's `page` flag and the legacy single-page cache follow the notes.
        void queryClient.invalidateQueries({ queryKey: ['pages'] })
        void queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey as unknown[]

                return key[0] === 'get' && key[1] === '/api/v1/organizations/{orgCode}/assets/{assetUid}'
            },
        })
    }
}

export function useCreateAssetNote(org: string, assetUid: string) {
    const invalidate = useInvalidateKnowledge(org)

    return useMutation({
        mutationFn: async (body: NoteInput & { title: string }): Promise<KnowledgeNote> => {
            const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/assets/{assetUid}/notes', {
                params: { path: { orgCode: org, assetUid } },
                body,
            })
            if (error) fail(error)
            return data!.note
        },
        onSuccess: invalidate,
    })
}

export function useUpdateAssetNote(org: string, assetUid: string) {
    const invalidate = useInvalidateKnowledge(org)

    return useMutation({
        mutationFn: async ({ noteUid, ...body }: NoteInput & { noteUid: string }): Promise<KnowledgeNote> => {
            const { data, error } = await apiClient.PATCH('/api/v1/organizations/{orgCode}/assets/{assetUid}/notes/{noteUid}', {
                params: { path: { orgCode: org, assetUid, noteUid } },
                body,
            })
            if (error) fail(error)
            return data!.note
        },
        onSuccess: invalidate,
    })
}

export function useDeleteAssetNote(org: string, assetUid: string) {
    const invalidate = useInvalidateKnowledge(org)

    return useMutation({
        mutationFn: async (noteUid: string): Promise<void> => {
            const { error } = await apiClient.DELETE('/api/v1/organizations/{orgCode}/assets/{assetUid}/notes/{noteUid}', {
                params: { path: { orgCode: org, assetUid, noteUid } },
            })
            if (error) fail(error)
        },
        onSuccess: invalidate,
    })
}

export function useCreateOrganizationNote(org: string) {
    const invalidate = useInvalidateKnowledge(org)

    return useMutation({
        mutationFn: async (body: NoteInput & { title: string }): Promise<KnowledgeNote> => {
            const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/knowledge', {
                params: { path: { orgCode: org } },
                body,
            })
            if (error) fail(error)
            return data!.note
        },
        onSuccess: invalidate,
    })
}

export function useUpdateOrganizationNote(org: string) {
    const invalidate = useInvalidateKnowledge(org)

    return useMutation({
        mutationFn: async ({ noteUid, ...body }: NoteInput & { noteUid: string }): Promise<KnowledgeNote> => {
            const { data, error } = await apiClient.PATCH('/api/v1/organizations/{orgCode}/knowledge/{noteUid}', {
                params: { path: { orgCode: org, noteUid } },
                body,
            })
            if (error) fail(error)
            return data!.note
        },
        onSuccess: invalidate,
    })
}

export function useDeleteOrganizationNote(org: string) {
    const invalidate = useInvalidateKnowledge(org)

    return useMutation({
        mutationFn: async (noteUid: string): Promise<void> => {
            const { error } = await apiClient.DELETE('/api/v1/organizations/{orgCode}/knowledge/{noteUid}', {
                params: { path: { orgCode: org, noteUid } },
            })
            if (error) fail(error)
        },
        onSuccess: invalidate,
    })
}

/*
 * Knowledge proposals (spec 2026-11-01-01): a proposed create, update or retire
 * of a note, decided by a `knowledge.write` holder on the target. Drafts come
 * from a sent reply through the `knowledge_suggest` job.
 */

export type KnowledgeProposal = components['schemas']['KnowledgeProposal']
export type KnowledgeDraft = components['schemas']['KnowledgeDraft']
export type KnowledgeDraftStatus = components['schemas']['KnowledgeDraftStatus']
export type CreateKnowledgeProposalRequest = components['schemas']['CreateKnowledgeProposalRequest']
export type KnowledgeProposalEdits = components['schemas']['KnowledgeProposalEdits']
export type NoteVersionOrigin = components['schemas']['NoteVersionOrigin']

/** A stale approve's 409: the error carries the proposal with the three texts. */
export class ProposalStaleError extends Error {
    proposal: KnowledgeProposal | undefined

    constructor(proposal: KnowledgeProposal | undefined) {
        super('stale')
        this.proposal = proposal
    }
}

const proposalKeys = {
    all: (org: string) => ['knowledge', org, 'proposals'] as const,
    list: (org: string, status: string) => ['knowledge', org, 'proposals', 'list', status] as const,
    count: (org: string) => ['knowledge', org, 'proposals', 'count'] as const,
    one: (org: string, uid: string) => ['knowledge', org, 'proposals', 'one', uid] as const,
    draft: (org: string, jobUid: string) => ['knowledge', org, 'proposals', 'draft', jobUid] as const,
}

export function useKnowledgeProposals(org: string, status: string, enabled = true) {
    return useQuery({
        queryKey: proposalKeys.list(org, status),
        enabled,
        queryFn: async (): Promise<KnowledgeProposal[]> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/knowledge/proposals', {
                params: { path: { orgCode: org }, query: { status: status || undefined } },
            })
            if (error) fail(error)
            return data?.proposals ?? []
        },
    })
}

export function useKnowledgeProposal(org: string, uid: string | undefined) {
    return useQuery({
        queryKey: proposalKeys.one(org, uid ?? ''),
        enabled: !!uid,
        queryFn: async (): Promise<KnowledgeProposal> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/knowledge/proposals/{proposalUid}', {
                params: { path: { orgCode: org, proposalUid: uid! } },
            })
            if (error) fail(error)
            return data!.proposal
        },
    })
}

/** The pending proposals the caller may decide: the tab and sidebar badge. */
export function useKnowledgeProposalCount(org: string, enabled: boolean) {
    return useQuery({
        queryKey: proposalKeys.count(org),
        enabled: enabled && !!org,
        refetchOnWindowFocus: true,
        queryFn: async (): Promise<number> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/knowledge/proposals/count', {
                params: { path: { orgCode: org } },
            })
            if (error) fail(error)
            return data?.pending ?? 0
        },
    })
}

function useInvalidateProposals(org: string) {
    const invalidate = useInvalidateKnowledge(org)
    const queryClient = useQueryClient()

    return () => {
        invalidate()
        // The thread carries its pending proposals (the suggestion card).
        void queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey as unknown[]

                return key[0] === 'get' && String(key[1] ?? '').startsWith('/api/v1/organizations/{orgCode}/conversations')
            },
        })
    }
}

export function useCreateKnowledgeProposal(org: string) {
    const invalidate = useInvalidateProposals(org)

    return useMutation({
        mutationFn: async (body: CreateKnowledgeProposalRequest): Promise<{ proposal: KnowledgeProposal; applied: boolean }> => {
            const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/knowledge/proposals', {
                params: { path: { orgCode: org } },
                body,
            })
            if (error) fail(error)
            return data!
        },
        onSuccess: invalidate,
    })
}

export function useApproveKnowledgeProposal(org: string) {
    const invalidate = useInvalidateProposals(org)

    return useMutation({
        mutationFn: async ({
            uid,
            overrideStale,
            edits,
        }: {
            uid: string
            overrideStale?: boolean
            edits?: KnowledgeProposalEdits
        }): Promise<KnowledgeProposal> => {
            const { data, error, response } = await apiClient.POST(
                '/api/v1/organizations/{orgCode}/knowledge/proposals/{proposalUid}/approve',
                {
                    params: { path: { orgCode: org, proposalUid: uid } },
                    body: { ...(edits ?? {}), override_stale: overrideStale || undefined },
                },
            )
            if (error) {
                const e = error as { code?: string; proposal?: KnowledgeProposal }
                if (response.status === 409 && e.code === 'KNOWLEDGE_PROPOSAL_STALE') throw new ProposalStaleError(e.proposal)
                fail(error)
            }
            return data!.proposal
        },
        // A stale answer moved the proposal too.
        onSettled: invalidate,
    })
}

export function useDecideKnowledgeProposal(org: string, action: 'reject' | 'dismiss' | 'withdraw') {
    const invalidate = useInvalidateProposals(org)

    return useMutation({
        mutationFn: async ({ uid, reason }: { uid: string; reason?: string }): Promise<KnowledgeProposal> => {
            const params = { path: { orgCode: org, proposalUid: uid } }
            const { data, error } =
                action === 'reject'
                    ? await apiClient.POST('/api/v1/organizations/{orgCode}/knowledge/proposals/{proposalUid}/reject', {
                          params,
                          body: { reason: reason ?? '' },
                      })
                    : action === 'dismiss'
                      ? await apiClient.POST('/api/v1/organizations/{orgCode}/knowledge/proposals/{proposalUid}/dismiss', { params })
                      : await apiClient.POST('/api/v1/organizations/{orgCode}/knowledge/proposals/{proposalUid}/withdraw', { params })
            if (error) fail(error)
            return data!.proposal
        },
        onSuccess: invalidate,
    })
}

/** Queues the draft of a note from a sent reply; answers the job to poll. */
export function useRequestKnowledgeDraft(org: string) {
    return useMutation({
        mutationFn: async (body: { conversation_uid: string; message_uid: string }): Promise<string> => {
            const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/knowledge/proposals/drafts', {
                params: { path: { orgCode: org } },
                body,
            })
            if (error) fail(error)
            return data!.job_uid
        },
    })
}

/** Polls a draft job until it is done or failed. */
export function useKnowledgeDraft(org: string, jobUid: string | null) {
    return useQuery({
        queryKey: proposalKeys.draft(org, jobUid ?? ''),
        enabled: !!jobUid,
        refetchInterval: (query) => (query.state.data?.status === 'pending' ? 1000 : false),
        queryFn: async (): Promise<KnowledgeDraftStatus> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/knowledge/proposals/drafts/{jobUid}', {
                params: { path: { orgCode: org, jobUid: jobUid! } },
            })
            if (error) fail(error)
            return data!
        },
    })
}

/** One note: an asset note through its asset, an organization note directly. */
export function useNote(org: string, assetUid: string | undefined, noteUid: string, enabled: boolean) {
    return useQuery({
        queryKey: ['knowledge', org, 'note', assetUid ?? '', noteUid] as const,
        enabled: enabled && !!noteUid,
        queryFn: async (): Promise<KnowledgeNote> => {
            const { data, error } = assetUid
                ? await apiClient.GET('/api/v1/organizations/{orgCode}/assets/{assetUid}/notes/{noteUid}', {
                      params: { path: { orgCode: org, assetUid, noteUid } },
                  })
                : await apiClient.GET('/api/v1/organizations/{orgCode}/knowledge/{noteUid}', {
                      params: { path: { orgCode: org, noteUid } },
                  })
            if (error) fail(error)
            return data!.note
        },
    })
}
